import { useState, useRef } from "react";
import axios from "axios";
import { Plus, Trash2, Upload, User, Save, RefreshCw, X, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2, 8));

const emptyMember = () => ({
  id: uid(),
  name: "",
  role: "",
  bio: "",
  photo_url: "",
  languages: "",
  specialties: "",
  email: "",
  instagram: "",
  linkedin: "",
});

/**
 * TeamEditor — CRUD für team_members einer Firma.
 *   Props:
 *    - companyId (required)
 *    - initialMembers (array)
 *    - onChange?(members)    — callback wenn Liste geändert (optional, für externen State)
 *    - onSave?()             — callback nach erfolgreichem Save
 */
export const TeamEditor = ({ companyId, initialMembers = [], onSave }) => {
  const [members, setMembers] = useState(
    Array.isArray(initialMembers) ? initialMembers.map(m => ({ ...emptyMember(), ...m })) : []
  );
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRefs = useRef({});

  const add = () => setMembers(ms => [...ms, emptyMember()]);
  const remove = (idx) => setMembers(ms => ms.filter((_, i) => i !== idx));
  const update = (idx, upd) => setMembers(ms => ms.map((m, i) => (i === idx ? { ...m, ...upd } : m)));
  const move = (idx, dir) => {
    setMembers(ms => {
      const arr = [...ms];
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return arr;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return arr;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const valid = members.filter(m => m.name.trim());
      const res = await axios.put(`${API}/companies/${companyId}`, { team_members: valid }, { withCredentials: true, timeout: 30000 });
      const updated = res.data?.team_members || valid;
      setMembers(updated.map(m => ({ ...emptyMember(), ...m })));
      toast.success("Team gespeichert – Website-Vorschau wird aktualisiert");
      if (onSave) onSave(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (idx) => {
    const m = members[idx];
    const input = fileInputRefs.current[m.id];
    if (!input?.files?.[0]) return;
    // Voraussetzung: Member muss zuerst in DB gespeichert sein
    const needsSave = !m.name.trim() || !members.filter(x => x.name.trim()).some(x => x.id === m.id);
    if (!m.name.trim()) {
      toast.error("Bitte zuerst einen Namen eingeben");
      return;
    }
    // Stelle sicher dass Member vor Upload gespeichert ist
    if (needsSave) {
      await save();
    } else {
      // Immer vorher persistieren damit member_id im Backend existiert
      await save();
    }
    setUploadingId(m.id);
    try {
      const fd = new FormData();
      fd.append("file", input.files[0]);
      const res = await axios.post(
        `${API}/companies/${companyId}/team-member/${m.id}/photo`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }
      );
      update(idx, { photo_url: res.data.photo_url });
      toast.success("Foto hochgeladen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingId(null);
      if (input) input.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="team-editor">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold">Team-Mitglieder</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Füge Mitarbeiter mit Foto, Rolle und Bio hinzu. Sie erscheinen auf der Team-Unterseite als Portrait-Grid.</p>
        </div>
        <Button size="sm" onClick={add} className="rounded-sm" data-testid="add-team-member-btn">
          <Plus size={13} className="mr-1" /> Mitglied
        </Button>
      </div>

      {members.length === 0 && (
        <div className="text-xs text-muted-foreground p-6 text-center border border-dashed border-border rounded-sm">
          Noch keine Mitarbeiter — ohne Team-Mitglieder fällt die Team-Seite auf einen Hinweis-Platzhalter zurück.
        </div>
      )}

      {members.map((m, idx) => (
        <div key={m.id} className="border border-border rounded-sm p-4 bg-card" data-testid={`team-member-row-${idx}`}>
          <div className="grid grid-cols-12 gap-3">
            {/* Foto-Spalte */}
            <div className="col-span-12 md:col-span-3 flex flex-col items-center gap-2">
              <div className="relative w-full aspect-[3/4] rounded-sm bg-muted overflow-hidden border border-border">
                {m.photo_url ? (
                  <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <User size={40} strokeWidth={1.2} />
                  </div>
                )}
                {uploadingId === m.id && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <RefreshCw className="animate-spin text-white" size={20} />
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={(el) => { fileInputRefs.current[m.id] = el; }}
                onChange={() => uploadPhoto(idx)}
                className="hidden"
                data-testid={`team-photo-input-${idx}`}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRefs.current[m.id]?.click()}
                disabled={uploadingId === m.id || !m.name.trim()}
                className="rounded-sm text-xs w-full"
                data-testid={`team-photo-btn-${idx}`}
                title={!m.name.trim() ? "Zuerst Name eingeben" : ""}
              >
                <Upload size={11} className="mr-1" /> {m.photo_url ? "Foto tauschen" : "Foto hochladen"}
              </Button>
            </div>

            {/* Daten-Spalte */}
            <div className="col-span-12 md:col-span-9 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name *</Label>
                  <Input value={m.name} onChange={e => update(idx, { name: e.target.value })} placeholder="Anna Müller" className="rounded-sm h-8 text-sm" data-testid={`team-name-${idx}`} />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Rolle / Position</Label>
                  <Input value={m.role} onChange={e => update(idx, { role: e.target.value })} placeholder="Senior Stylistin" className="rounded-sm h-8 text-sm" data-testid={`team-role-${idx}`} />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Kurz-Bio</Label>
                <Textarea value={m.bio} onChange={e => update(idx, { bio: e.target.value })} rows={2} placeholder="1-2 Sätze zu Person, Background oder Philosophie" className="rounded-sm text-sm" data-testid={`team-bio-${idx}`} />
              </div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Spezialisierungen</Label>
                  <Input value={m.specialties} onChange={e => update(idx, { specialties: e.target.value })} placeholder="Balayage, Bob-Schnitt, Hochsteckfrisuren" className="rounded-sm h-8 text-sm" data-testid={`team-spec-${idx}`} />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Durch Komma getrennt</p>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Sprachen</Label>
                  <Input value={m.languages} onChange={e => update(idx, { languages: e.target.value })} placeholder="Deutsch, Englisch, Italienisch" className="rounded-sm h-8 text-sm" data-testid={`team-lang-${idx}`} />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">E-Mail</Label>
                  <Input type="email" value={m.email} onChange={e => update(idx, { email: e.target.value })} placeholder="anna@salon.de" className="rounded-sm h-8 text-sm" />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Instagram</Label>
                  <Input value={m.instagram} onChange={e => update(idx, { instagram: e.target.value })} placeholder="instagram.com/anna" className="rounded-sm h-8 text-sm" />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">LinkedIn</Label>
                  <Input value={m.linkedin} onChange={e => update(idx, { linkedin: e.target.value })} placeholder="linkedin.com/in/anna" className="rounded-sm h-8 text-sm" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-7 w-7 p-0 text-muted-foreground" title="Nach oben">↑</Button>
                  <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === members.length - 1} className="h-7 w-7 p-0 text-muted-foreground" title="Nach unten">↓</Button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(idx)} className="h-7 text-xs text-muted-foreground hover:text-red-600" data-testid={`remove-team-${idx}`}>
                  <Trash2 size={11} className="mr-1" /> Entfernen
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="save-team-btn">
          {saving ? <><RefreshCw size={13} className="mr-1 animate-spin" /> Speichert...</> : <><Save size={13} className="mr-1" /> Team speichern</>}
        </Button>
      </div>
    </div>
  );
};
